RivalIQ.module('state', function (state, App, Backbone, Marionette, $, _, moment) {

    'use strict';

    /**
     * The current account state of the application.
     * @type {Backbone.Model}
     */
    const account = state.account = new RivalIQ.models.Account();

    state.utils.overrideStateGet(account, {
        trialExpirationDate: moment.utc
    });
    state.utils.overrideStateSet(account);

    // NOTE we want to set up some trigger behavior for certain state properties
    account.listenTo(account, 'change:trialReportCount', function () {

        if (account.isAtTrialReportLimit()) {

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.HitTrialReportLimit);

            $.post('/api/triggers', {
                type: 'email',
                trigger_type: 'trial_report_limit_reached'
            });
        }
    });

    /**
     * Returns a boolean indicating whether the account is an administrator, or an administrator impersonating other users.
     * @returns {boolean}
     */
    account.isAdmin = function () {
        const me = this;
        return (me.get('groupId') === 1 || me.isImpersonated());
    };

    /**
     * Returns a boolean indicating whether the account is impersonated.
     */
    account.isImpersonated = function () {
        return state.view.get('isImpersonating');
    };

    /**
     * Returns a boolean indicating whether the account is impersonated is an admin.
     */
    account.isImpersonatedAdmin = function () {

        const me = this;
        return (me.get('impersonatedAccountGroupId') === 1);
    };

    /**
     * Returns a boolean indicating whether the account is currently trialing.
     * @returns {*}
     */
    account.isTrialing = function () {

        const me = this;
        return me.get('isInTrial');
    };

    /**
     * Returns a boolean indicating whether the account is currently trialing and is on the Trial plan.
     * @returns {*}
     */
    account.isTrialerWithTrialPlan = function () {
        const me = this;
        return me.isTrialing() && me.get('hasTrialPlan');
    };

    /**
     * Returns a boolean indicating whether the account is at the report limit for their plan.
     * @returns {*}
     */
    account.isAtTrialReportLimit = function () {

        const me = this;
        return (me.hasTrialReportLimits() && me.get('trialReportCount') >= me.get('trialReportLimit'));
    };

    account.getDaysRemainingInTrial = function () {
        // NOTE we add 12 hours because our end time is generated 14 days
        // from sign up time, and therefore would never read 14 days
        // (e.g., 13 days) without adding a bit of slop to the end...
        //
        // @doc says this number (12 hours) is slightly magical, so it
        // might eventually be worth revisiting in earnest
        //
        // note: this logic is duplicated in the mixins/subscription.jade, so any changes here
        // should be made there as well.
        const me = this;
        const trialEndTime = me.get('trialExpirationDate').add(12, 'hours');
        return Math.max(trialEndTime.diff(moment.utc(), 'days'), 0);
    };

    account.isUnderActiveContract = function () {
        const me = this;
        const contract = me.get('contract');
        const contractStatus = _.get(contract, ['Status']);
        const contractPurchaseMethod = _.get(contract, ['Purchase_Method__c']);
        return (contractStatus === 'Activated' && contractPurchaseMethod === 'Contract');
    };

    account.wasUnderActiveContract = function () {
        const me = this;
        const contract = me.get('lastContract');
        const contractStatus = _.get(contract, ['Status']);
        const contractPurchaseMethod = _.get(contract, ['Purchase_Method__c']);
        return (contractStatus === 'Activated' && contractPurchaseMethod === 'Contract');
    };

    account.getDaysUntilContractRenewal = function () {
        const me = this;

        if (!me.isUnderActiveContract() && !me.wasUnderActiveContract()) {
            return null;
        }

        const contract = me.get('lastContract');
        const contractEndDate = _.get(contract, ['Contract_End_Date__c']);

        if (!contractEndDate) {
            return null;
        }

        return moment.utc(contractEndDate).diff(moment.utc(), 'days');
    };

    account.contractRequiresRenewal = function () {
        const me = this;

        if (!account.isUnderActiveContract() && !account.wasUnderActiveContract()) {
            return false;
        }

        const includeInAutomation = me.get('includeInRenewalAutomation');
        if (!includeInAutomation) {
            return false;
        }

        const contract = me.get('lastContract');

        const renew = _.get(contract, ['Renew__c']);
        const renewed = _.get(contract, ['Renewed__c']);

        return (renew && !renewed);
    };

    account.showPaymentOverdueWarning = function () {
        const me = this;
        return (Boolean(me.get('showPaymentOverdueWarning')));
    };

    account.canPayOnline = function () {
        const me = this;

        // NOTE we need to honor Rival IQ admin setting, see #22505
        if (me.get('paymentViaInvoice')) {
            return false;
        }

        const contract = me.get('contract');
        if (contract) {
            const contractBillingMethod = _.get(contract, ['Billing_Method__c']);
            return (contractBillingMethod === 'Credit Card');
        }

        return Boolean(me.get('hasStripeId'));
    };

    account.hasFailedPayment = function () {
        const me = this;
        return Boolean(me.get('hasFailedPayment'));
    };

    account.trialExpired = function () {
        const me = this;
        return me.isTrialing() && me.get('trialExpirationDate').diff(moment.utc()) < 0;
    };

    /**
     * Increments a count property.
     * @param property The property name to update (will be appended with Count).
     */
    account.incrementPropertyCount = function (property) {

        const me = this;
        const name = `${property}Count`;
        me.set(name, me.get(name) + 1);
    };

    /**
     * Increments the trial report count (NOTE: this will persist).
     */
    account.incrementTrialReportCount = _.bind(account.incrementPropertyCount, account, 'trialReport');

    /**
     * Increments the lifetime company add count (NOTE: this is for the UI only).
     */
    account.incrementLifetimeCompanyAddCount = _.bind(account.incrementPropertyCount, account, 'lifetimeCompanyAdd');

    account.getExpiredVisitsRemaining = function () {
        const me = this;
        return Math.max(RivalIQ.state.product.get('expiredTrialMaxVisits') - me.get('expiredTrialVisitCount'), 0);
    };

    /**
     * Returns a boolean indicating if this test is in group A.
     * @returns {boolean}
     */
    account.isTestA = function () {

        const me = this;
        return Boolean(me.get('testGetsA'));
    };

    /**
     * Returns a boolean indicating if this test is in group B.
     * @returns {boolean}
     */
    account.isTestB = function () {

        const me = this;
        return !(me.isTestA());
    };

    /**
     * Returns a boolean indicating if this account has report limits.
     * @returns {boolean}
     */
    account.hasTrialReportLimits = function () {

        const me = this;
        // NOTE we only care about trialing users for now, but new plans might
        // emerge with similar constraints w/r/t reporting
        return me.isTrialerWithTrialPlan();
    };

    (function () {

        /**
         * Returns a boolean indicating if this account has a plan option.
         * @param option The plan option to test.
         * @returns {boolean}
         */
        account.hasPlanOption = function (option) {

            const me = this;
            return _.get(me.get('plan'), option, false);
        };

        const options = [
            {
                option: 'extended_social_content_history',
                name: 'ExtendedSocialContentHistory'
            }
        ];

        _.each(options, function (config) {

            account[`has${config.name}`] = _.bind(account.hasPlanOption, account, config.option);
        });

    }());

    account.channelSupported = function (channel) {
        const me = this;
        const props = _.extend({}, me.attributes, RivalIQ.state.plan.attributes);
        return RivalIQ.props.channelSupported(props, channel);
    };

    account.channelDisplay = function (channel) {
        const me = this;
        const props = _.extend({}, me.attributes, RivalIQ.state.landscape.attributes, RivalIQ.state.plan.attributes);
        return RivalIQ.props.channelDisplay(props, channel);
    };

    account.channelDisplayWithUpsell = function (channel) {
        const me = this;
        const props = _.extend({}, me.attributes, RivalIQ.state.landscape.attributes, RivalIQ.state.plan.attributes);
        return RivalIQ.props.channelDisplayWithUpsell(props, channel);
    };

    account.getSupportedSourcesMask = function () {
        const me = this;
        return me.get('plan').supported_sources;
    };

    account.getTimePeriodMask = function () {
        const me = this;
        return me.get('plan').supported_main_time_periods;
    };

    account.durationSupported = function (duration) {
        const props = RivalIQ.state.plan.attributes;
        return RivalIQ.props.durationSupported(props, duration);
    };

    account.timePeriodRestricted = function () {
        const props = RivalIQ.state.plan.attributes;
        return RivalIQ.props.timePeriodRestricted(props);
    };

    /**
     * Returns a boolean indicating whether the account is in a beta.
     * @param beta The beta identifier to test.
     * @param [options] An optional set of options.
     */
    account.inBeta = function (beta, options) {

        const me = this;

        const isImpersonating = state.view.get('isImpersonating');
        const isImpersonatingAdmin = me.isImpersonatedAdmin();
        const enabled = _.get(me.get('betaFeatures'), beta, false);
        const adminEnabled = (_.get(options, 'adminEnabled', false) && me.isAdmin());

        // NOTE we want to return true when:
        //
        //    1. the account, whether impersonated or not, has explicit access
        //    2. the feature is enabled for administrators
        //       a. and we are not impersonating (active user is an admin)
        //       b. and we are impersonating an admin
        return (enabled || (adminEnabled && (!isImpersonating || isImpersonatingAdmin)));
    };

    account.inRegistration = function () {
        const me = this;
        return me.get('status') === RivalIQ.enums.account.status.REGISTRATION;
    };

    account.isPaid = function (options) {
        const me = this;
        options = options || {};
        const paidStatus = me.get('accountConversionStatus') === RivalIQ.enums.account.conversionStatus.PAID;

        return paidStatus && (options.withStripe ? me.get('hasStripeId') : true);
    };

    account.officiallyChurned = function () {
        const me = this;
        return me.get('accountConversionStatus') === RivalIQ.enums.account.conversionStatus.CHURNED;
    };

    account.selfChurned = function () {
        const me = this;
        return Boolean(me.get('selfChurned'));
    };

    // NOTE see #22620 as to why we need this
    account.hasLimitedLandscapeCapability = function () {
        const me = this;
        return (me.trialExpired() || account.isCancelled());
    };

    // Account is considered churned from a ui perspective if
    // The account has self-churned or the account has been marked as churned
    // by an admin and the account has not resubscribed themselves
    account.churned = function () {
        const me = this;
        return me.selfChurned() || me.officiallyChurned();
    };

    account.churnDate = function () {
        const me = this;

        if (me.officiallyChurned()) {
            return moment.utc(me.get('churnedAt'));
        }

        if (me.selfChurned() && me.get('churnedAt')) {
            return moment.utc(me.get('churnedAt'));
        }

        if (!me.trialExpired()) {
            return me.get('trialExpirationDate');
        }

        return null;
    };

    account.isCancelled = function () {
        const me = this;
        return me.churned() && moment.utc(me.get('churnedAt')).isBefore(moment.utc());
    };

    account.canCustomizeBranding = function () {
        const me = this;
        return Boolean(me.get('plan').has_white_label);
    };

    account.isKeyGeo = function () {
        const me = this;
        return (me.get('leadScore').keyGeo);
    };

    account.isQuidAccountGroup = function () {
        const me = this;
        const accountGroupId = me.get('groupId');
        const SOLD_VIA_QUID_ACCOUNT_GROUP_ID = 80;
        const TRANSFERRED_TO_QUID_ACCOUNT_GROUP_ID = 83;
        return (accountGroupId === SOLD_VIA_QUID_ACCOUNT_GROUP_ID || accountGroupId === TRANSFERRED_TO_QUID_ACCOUNT_GROUP_ID);
    };

    /**
     * A property map transforming settings into state, and state
     * into persistence.
     * @type {*[]}
     */
    const statePropertyMap = [
        {
            settingProperty: 'accountId',
            stateProperty: 'id',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountName',
            stateProperty: 'name',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountGroupId',
            stateProperty: 'groupId',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountHubId',
            stateProperty: 'accountHubId',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'primaryAccountId',
            path: null,
            stateProperty: 'primaryAccountId',
            immutable: true
        },
        {
            settingProperty: 'isAgency',
            path: null,
            stateProperty: 'isAgency',
            immutable: true
        },
        {
            settingProperty: 'hasDiscount',
            path: null,
            stateProperty: 'hasDiscount',
            immutable: true
        },
        {
            settingProperty: 'trial',
            stateProperty: 'isInTrial',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'hasTrialPlan',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'trialendtime',
            stateProperty: 'trialExpirationDate',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'activated_at',
            stateProperty: 'activatedAt',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'lifetimeCompanyAddCount',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'expiredTrialVisitCount',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'selfChurned',
            default: false,
            immutable: true
        },
        {
            settingProperty: 'enforceCompanyPlanLimit',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'resetAt',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'followQuotaInfo',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'companyLimits',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'listeningSavedSearchCount',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'trialReportCount',
            persistenceProperty: 'trial_report_count',
            path: null
        },
        {
            settingProperty: 'trialReportLimit',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'plan',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountStripeCoupon',
            stateProperty: 'coupon',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'testGetsA',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'impersonatedAccountGroupId',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'email',
            stateProperty: 'emailSettings',
            default: {}
        },
        {
            settingProperty: 'brandCustomization'
        },
        {
            settingProperty: 'beta_features',
            path: null,
            stateProperty: 'betaFeatures',
            immutable: true
        },
        {
            settingProperty: 'notifications',
            default: {}
        },
        {
            settingProperty: 'alertMsg',
            path: null,
            default: {},
            immutable: true
        },
        {
            settingProperty: 'accountStatus',
            stateProperty: 'status',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountChurnedAt',
            stateProperty: 'churnedAt',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountEmailVerifiedAt',
            stateProperty: 'accountEmailVerifiedAt',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountConversionStatus',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountHasStripeId',
            stateProperty: 'hasStripeId',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountPlanOverrides',
            stateProperty: 'planOverrides',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'historyLimits',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'hideInsightsOnExport',
            stateProperty: 'hideInsightsOnExport',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'companyLimits',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'churnDate',
            path: null,
            default: null,
            immutable: true
        },
        {
            settingProperty: 'testVariant',
            path: null,
            default: null,
            immutable: true
        },
        {
            settingProperty: 'accountSettings',
            path: null,
            stateProperty: 'settings',
            immutable: true
        },
        {
            settingProperty: 'currentStripeCoupon',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'redactedSources',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'dashboards',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'leadScore',
            stateProperty: 'leadScore',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'isInExplorerCloneGroup',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'isInNBQCustomerGroup',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'widgets',
            default: {}
        },
        {
            settingProperty: 'initialQual',
            path: null,
            immutable: false
        },
        {
            settingProperty: 'csm',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'contract',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'lastContract',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'includeInRenewalAutomation',
            stateProperty: 'includeInRenewalAutomation',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'renewalOpportunity',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountPaymentViaInvoice',
            stateProperty: 'paymentViaInvoice',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'accountHasFailedPayment',
            stateProperty: 'hasFailedPayment',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'showPaymentOverdueWarning',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'appLogoPath',
            stateProperty: 'appLogoPath',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'hasValidQuidMonitorToken',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'hasCompanionMode',
            stateProperty: 'hasCompanionMode',
            path: null,
            immutable: true
        }
    ];

    function initializeAccountStateFromProperties(account, properties) {

        state.utils.initializeState('account', 'accountSettings', statePropertyMap, account, properties);


        if (RivalIQ.state.account.trialExpired()) {
            // If the user is an expired trialer, they're only allowed to see last 7 days.
            RivalIQ.state.view.set('timePeriod', RivalIQ.state.timePeriods.getByType(RivalIQ.enums.timePeriod.type.LAST_SEVEN_DAYS));
        }

    }

    (function () {

        let hasInitialized = false;

        account.initialize = function (properties) {

            if (hasInitialized) {

                throw 'not supported: account state cannot be initialized more than once.';
            }

            initializeAccountStateFromProperties(this, properties);

            hasInitialized = true;
        };

    }());

}, window.moment);
